// Coordinates a single open PressableHint within a scoped subtree (e.g. one result card list).

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

type PressableHintContextValue = {
  openHintId: string | null;
  toggleHint: (hintId: string) => void;
  closeHints: () => void;
};

const PressableHintContext = createContext<PressableHintContextValue | null>(null);

export const PressableHintProvider = ({ children }: { children: ReactNode }) => {
  const [openHintId, setOpenHintId] = useState<string | null>(null);

  const toggleHint = useCallback((hintId: string): void => {
    setOpenHintId((prev) => (prev === hintId ? null : hintId));
  }, []);

  const closeHints = useCallback((): void => {
    setOpenHintId(null);
  }, []);

  const value = useMemo(
    () => ({ openHintId, toggleHint, closeHints }),
    [closeHints, openHintId, toggleHint],
  );

  return <PressableHintContext.Provider value={value}>{children}</PressableHintContext.Provider>;
};

export const usePressableHintScope = (): PressableHintContextValue | null => useContext(PressableHintContext);
